import React from 'react';
import styled from 'styled-components';
import { animated, SpringValue } from 'react-spring';
import BagetImg from './images/baget.png';
import BagetRetinaImg from './images/baget@2x.png';
import BananaImg from './images/banana.png';
import BananaRetinaImg from './images/banana@2x.png';
import GelImg from './images/gel.png';
import GelRetinaImg from './images/gel@2x.png';
import PepperImg from './images/pepper.png';
import PepperRetinaImg from './images/pepper@2x.png';
import CheeseBigImg from './images/cheese-b.png';
import CheeseBigRetinaImg from './images/cheese-b@2x.png';
import CheeseSmImg from './images/cheese-s.png';
import CheeseSmRetinaImg from './images/cheese-s@2x.png';
import CrackerBigImg from './images/cracker-b.png';
import CrackerBigRetinaImg from './images/cracker-b@2x.png';
import CrackerSmImg from './images/cracker-s.png';
import CrackerSmRetinaImg from './images/cracker-s@2x.png';
import PaperImg from './images/paper.png';
import PaperRetinaImg from './images/paper@2x.png';
import PepperMobileImg from './images/pepper-mobile.png';
import PepperMobileRetinaImg from './images/pepper-mobile@2x.png';
import { mediaSmDown, mediaSmUp } from 'theme/helpers/css';

const Animation = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  max-width: 1920px;
  left: 50%;
  transform: translateX(-50%);
`;

const MIN_COEF = 0.8;
const MAX_COEF = 1.2;
const ABSOLUTE_W = 1440;
const ABSOLUTE_H = 810;

const getPosition = (value: number, absolute_value: number) => {
  return `${Math.round((value * 100) / absolute_value)}%`;
};

const Picture = styled.div<{ $width: number }>`
  position: absolute;
  ${({ $width }) =>
    $width &&
    `width: clamp(${$width * MIN_COEF}px, ${Math.round(($width * 100) / ABSOLUTE_W)}%, ${$width * MAX_COEF}px);`}

  picture,
  img {
    width: 100%;
  }

  ${mediaSmDown`
    display: none;
  `}
`;

const PictureBaget = styled(Picture)`
  bottom: -20px;
  left: 42%;
`;

const PictureGel = styled(Picture)`
  bottom: ${getPosition(86, ABSOLUTE_H)};
  right: ${getPosition(88, ABSOLUTE_W)};
`;

const PicturePaper = styled(Picture)`
  bottom: ${getPosition(58, ABSOLUTE_H)};
  right: ${getPosition(41, ABSOLUTE_W)};
`;

const PictureBanana = styled(Picture)`
  top: ${getPosition(86, ABSOLUTE_H)};
  left: ${getPosition(30, ABSOLUTE_W)};
`;

const PicturePepper = styled(Picture)`
  bottom: -20px;
  left: ${getPosition(172, ABSOLUTE_W)};
`;

const PictureCheeseBig = styled(Picture)`
  top: -20px;
  left: ${getPosition(582, ABSOLUTE_W)};
`;

const PictureCheeseSm = styled(Picture)`
  top: ${getPosition(467, ABSOLUTE_H)};
  left: ${getPosition(39, ABSOLUTE_W)};
`;

const PictureCrackerBig = styled(Picture)`
  top: ${getPosition(84, ABSOLUTE_H)};
  right: ${getPosition(86, ABSOLUTE_W)};
`;

const PictureCrackerSm = styled(Picture)`
  top: ${getPosition(33, ABSOLUTE_H)};
  right: ${getPosition(253, ABSOLUTE_W)};
`;

const PicturePepperMobile = styled.picture`
  position: absolute;
  bottom: 0;
  left: 0;

  picture,
  img {
    width: 100%;
  }

  ${mediaSmUp`
    display: none;
  `}
`;

const fastTranslate = (x: number, y: number) => `translate3d(${x / 12}px,${y / 12}px,0)`;
const normalTranslate = (x: number, y: number) => `translate3d(${x / 20}px,${y / 20}px,0)`;
const slowTranslate = (x: number, y: number) => `translate3d(${x / 30}px,${y / 30}px,0)`;
const superSlowTranslate = (x: number, y: number) => `translate3d(${x / 40}px,${y / 40}px,0)`;
interface IAnimatedBg {
  animationProps: { xy: SpringValue<number[]> };
}

const AnimatedBg: React.FC<IAnimatedBg> = ({ animationProps }) => {
  const { xy } = animationProps;
  const superSlowTransform = xy.to(superSlowTranslate);
  const slowTransform = xy.to(slowTranslate);
  const normalTransform = xy.to(normalTranslate);
  const fastTransform = xy.to(fastTranslate);
  return (
    <Animation>
      <PicturePaper $width={98}>
        <animated.div style={{ transform: superSlowTransform }}>
          <source srcSet={`${PaperRetinaImg} 2x, ${PaperImg} 1x`} />
          <img src={PaperImg} alt="" />
        </animated.div>
      </PicturePaper>
      <PictureCheeseSm $width={130}>
        <animated.div style={{ transform: superSlowTransform }}>
          <source srcSet={`${CheeseSmRetinaImg} 2x, ${CheeseSmImg} 1x`} />
          <img src={CheeseSmImg} alt="" />
        </animated.div>
      </PictureCheeseSm>
      <PictureCrackerSm $width={76}>
        <animated.div style={{ transform: superSlowTransform }}>
          <source srcSet={`${CrackerBigRetinaImg} 2x, ${CrackerSmImg} 1x`} />
          <img src={CrackerSmImg} alt="" />
        </animated.div>
      </PictureCrackerSm>
      <PictureBaget $width={351}>
        <animated.div style={{ transform: slowTransform }}>
          <source srcSet={`${BagetRetinaImg} 2x, ${BagetImg} 1x`} />
          <img src={BagetImg} alt="" />
        </animated.div>
      </PictureBaget>
      <PictureCrackerBig $width={176}>
        <animated.div style={{ transform: slowTransform }}>
          <source srcSet={`${CrackerSmRetinaImg} 2x, ${CrackerBigImg} 1x`} />
          <img src={CrackerBigImg} alt="" />
        </animated.div>
      </PictureCrackerBig>
      <PictureCheeseBig $width={210}>
        <animated.div style={{ transform: normalTransform }}>
          <source srcSet={`${CheeseBigRetinaImg} 2x, ${CheeseBigImg} 1x`} />
          <img src={CheeseBigImg} alt="" />
        </animated.div>
      </PictureCheeseBig>
      <PictureGel $width={252}>
        <animated.div style={{ transform: normalTransform }}>
          <source srcSet={`${GelRetinaImg} 2x, ${GelImg} 1x`} />
          <img src={GelImg} alt="" />
        </animated.div>
      </PictureGel>
      <PicturePepper $width={306}>
        <animated.div style={{ transform: normalTransform }}>
          <source srcSet={`${PepperRetinaImg} 2x, ${PepperImg} 1x`} />
          <img src={PepperImg} alt="" />
        </animated.div>
      </PicturePepper>
      <PictureBanana $width={230}>
        <animated.div style={{ transform: fastTransform }}>
          <source srcSet={`${BananaRetinaImg} 2x, ${BananaImg} 1x`} />
          <img src={BananaImg} alt="" />
        </animated.div>
      </PictureBanana>
      <PicturePepperMobile>
        <source srcSet={`${PepperMobileRetinaImg} 2x, ${PepperMobileImg} 1x`} />
        <img src={PepperMobileImg} alt="" />
      </PicturePepperMobile>
    </Animation>
  );
};

export default AnimatedBg;
